// FT-65: Global Vessel Guard - MUST be first import
// This intercepts ALL localStorage writes to prevent cross-vessel contamination
import './utils/vesselGuard';

import './utils/legacyShims';
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./TailwindApp";

// CRITICAL: Clean up corrupted localStorage from previous migration bug
// This MUST run before anything else to prevent app crashes
import './utils/cleanupCorruptedLocalStorage';

// Import migration commands (exposes to window)
import './utils/migrations/runMigrations';

// FT-126: Run pre-departure checklist migration
import { migratePreDepartureToVoyageSettings } from './utils/migrations/migratePreDepartureToVoyageSettings';

// FT-129: Clean up rogue operations_<org>_<vessel> duplicate keys
import { cleanupRogueOperationsKey } from './utils/migrations/cleanupRogueOperationsKey';

// FT-219: Clean up duplicate phase operations in voyage_settings
import { cleanupDuplicatePhaseOps } from './utils/migrations/cleanupDuplicatePhaseOps';

// FT-219: Post-sync server cleanup for duplicate phase operations
import { registerServerCleanupListener } from './utils/migrations/cleanupDuplicatePhaseOpsServer';

// FT-481: Unlock stale operation_data locked:true entries left by pre-fix completeOperationV2
import { unlockStaleOpLocks } from './utils/migrations/unlockStaleOpLocks';

// FT-482: Unlock stale operation_data locked:true entries older than 5 min (age-based, no operation_complete required)
import { unlockAllStaleLocks_v2 } from './utils/migrations/unlockAllStaleLocks_v2';

// FT-484: Remove stuck trial voyage from IDB (client-only voyage blocking completion)
import { removeStuckVoyage_v3 } from './utils/migrations/removeStuckVoyage_v3';

// FT-489: Remove stale orphaned section(pre_arrival) entries that block the Arrive button
import { clearOrphanedSections_v1 } from './utils/migrations/clearOrphanedSections_v1';

// FT-135: Service worker registration for PWA caching
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Dev-only: register maintenance parity helpers at app boot
if (process.env.NODE_ENV !== 'production') {
  import('./dev/registerMaintenanceParity');
  import('./dev/registerDebugFunctions');
}

const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

// FT-126: Run pre-departure migration on app boot (before React mounts)
migratePreDepartureToVoyageSettings();

// FT-129: Clean up rogue duplicate operations keys (before React mounts)
cleanupRogueOperationsKey();

// FT-219: Clean up duplicate phase operations (before React mounts)
cleanupDuplicatePhaseOps();

// FT-219: Register post-sync server cleanup listener (waits for sync, then pushes clean data)
registerServerCleanupListener();

root.render(
  <StrictMode>
    <App />
  </StrictMode>
);

// FT-481 + FT-482 + FT-484 + FT-489: Run IDB migrations in sequence after root.render().
// v1 requires a matching operation_complete; v2 is age-based (catches entries v1
// misses); v3 removes a specific stuck trial voyage (client-only, not on server);
// clearOrphanedSections_v1 removes stale pre_arrival section entries that block Arrive.
// Sequenced with await so all migrations read-modify-write the same IDB records
// without racing each other. Outer IIFE is fire-and-forget — errors are caught
// inside each migration and never propagate here.
(async () => {
  await unlockStaleOpLocks();
  await unlockAllStaleLocks_v2();
  await removeStuckVoyage_v3();
  // FT-489: Remove stale orphaned section(pre_arrival) entries that block the Arrive button
  await clearOrphanedSections_v1();
})();

// FT-192: Register service worker with user-prompted updates
// When a new version is detected, dispatch event to show UpdateBanner
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    // FT-192: Dispatch custom event to show the update banner in TailwindApp
    console.log('[SW] New version detected - dispatching sw:update event');
    window.dispatchEvent(new CustomEvent('sw:update', {
      detail: { registration }
    }));
  },
  onSuccess: (registration) => {
    console.log('[SW] Content cached for offline use.');
  }
});
